import React, { useEffect, useState } from "react";

import SelectBox from "../../common/SelectBox";
import TextField from "../../common/TextField";
import { ReactComponent as CrowdfundingIcon } from "../../../assets/img/common/crowdfunding-icon.svg";
import AttachFileField from "../../common/AttachFileFiled";
import useApi from "../../../hooks/useApi";
import notify from "../../common/notify";
import { convertToMilliseconds } from "../../../utils/dateTool";
import {
  useGetAllUserQuery,
  useGetUserQuery,
} from "../../../redux/slices/api/userApi";
import { Bars } from "react-loader-spinner";
import MemberItem from "../../projects/MemberItem";
import { FaCheck } from "react-icons/fa";
import UserPlaceHolderImg from "../../../assets/img/common/user-placeholder-img.png";

import { useRef } from "react";
import { useSelector } from "react-redux";
import useOutsideClick from "../../../hooks/useOutsideClick";
import { currencyIcons } from "../../../utils/currencies";
import { CrowdfundingDurationOptions } from "../../../data/seletboxOptions";
import { selectGeneral } from "../../../redux/slices/generalSlice";

const Step3 = ({ formik, containerClass }) => {
  // Fetch all users and user profile data
  const { isLoading, data: allUsersData } = useGetAllUserQuery();
  const { isLoggedIn } = useSelector(selectGeneral);
  const { isLoading: getMeLoading, data } = useGetUserQuery(undefined, { skip: !isLoggedIn });
  let { user: userProfileData } = data ?? {};

  // State variables for filtered users, search value, and search suggestion visibility
  const [filteredUsers, setFilteredUser] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [showSearchSuggestion, setShowSearchSuggestion] = useState(false);

  // Set filtered users when all users data is loaded
  useEffect(() => {
    console.log(formik?.values?.category)
    if (!isLoading && allUsersData) {
      setFilteredUser(allUsersData);
    }
  }, [isLoading, allUsersData]);

  // Ref for search suggestion and handling outside click to close suggestions
  const suggestionRef = useRef(null);
  useOutsideClick(
    suggestionRef,
    () => setShowSearchSuggestion(false),
    null,
    showSearchSuggestion
  );

  // Close search suggestion when search value is empty
  useEffect(() => {
    if (!searchValue?.length) {
      setShowSearchSuggestion(false);
    }
  }, [searchValue]);

  // File upload handling and setting the attachment link in Formik
  const { loading: uploadFileLoading, PUBLIC_REQUEST } = useApi();
  const handleAttachFileChange = async (e) => {
    let formData = new FormData();
    formData.append("file", e.target.files[0]);
    const onSuccess = (data) => {
      notify(data?.message, "success");
      formik.setFieldValue("attachment", data?.link);
    };
    const onError = (error) => {
      if (error.status === 413) {
        notify(
          "The size of the uploaded file is large, please try again.",
          "error"
        );
      }
    };
    await PUBLIC_REQUEST({
      url: "/upload/",
      method: "post",
      data: formData,
      notifyError: true,
      onSuccess,
      onError,
    });
  };

  // Handle collaborator selection and update collaborators_queue in Formik
  const handleSelectCollaborator = (e, user) => {
    let cloneCollaborators = [...formik?.values?.collaborators_queue];
    if (e.target.checked) cloneCollaborators.push(user);
    else
      cloneCollaborators = cloneCollaborators?.filter(
        (item) => item._id !== user?._id
      );
    formik.setFieldValue("collaborators_queue", cloneCollaborators);
  };

  // Filter users based on search input
  const filterBySearch = (event) => {
    const query = event.target.value?.toLowerCase();
    var updatedUsers = [...allUsersData];
    updatedUsers = updatedUsers.filter((item) => {
      let { firstname, lastname, email, fundername } = item ?? {};
      return [firstname, lastname, email, fundername].some(
        (item) => item && item?.toLowerCase().includes(query)
      );
    });
    setSearchValue(query);
    setFilteredUser(updatedUsers);
    if (query?.length && !showSearchSuggestion) {
      setShowSearchSuggestion(true);
    }
  };

  // Handle invite click, validate email, and update collaborators in Formik
  const handleInviteClick = () => {
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(searchValue) || searchValue?.length < 6) {
      return notify("please enter a valid email!", "error");
    }
    let updatedEmails = [...formik?.values?.collaborators_email];
    let updatedCollaboratorsQueue = [...formik?.values?.collaborators_queue];
    let allCollaborators = [...formik?.values?.all_collaborators];
    let foundUser = allUsersData?.find(
      (user) => user?.email === searchValue?.trim()
    );
    let existInAllCollaborators = allCollaborators?.some(
      (user) =>
        user?.email === searchValue?.trim() || user === searchValue?.trim()
    );
    if (!existInAllCollaborators) {
      if (!!foundUser) {
        updatedCollaboratorsQueue?.push(foundUser);
        formik?.setFieldValue("collaborators_queue", updatedCollaboratorsQueue);
      } else {
        updatedEmails.push(searchValue);
        formik?.setFieldValue("collaborators_email", updatedEmails);
      }
      setSearchValue("");
    } else {
      notify("This email already exists in collaborators", "error");
    }
  };

  // Handle Enter key press in the search input
  const handleEnterSearch = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInviteClick();
    }
  };

  // Combine collaborator lists and update all_collaborators in Formik
  useEffect(() => {
    let { collaborators, collaborators_queue, collaborators_email } =
      formik?.values ?? {};

    formik?.setFieldValue("all_collaborators", [
      ...collaborators,
      ...collaborators_queue,
      ...collaborators_email,
    ]);
  }, [
    formik.values.collaborators,
    formik.values.collaborators_queue,
    formik.values.collaborators_email,
  ]);

  // Auto-complete funding_goal if it doesn't exist
  useEffect(() => {
    let { funding_goal, total_budget, visible } = formik.values ?? {};
    if (!funding_goal && !visible && funding_goal <= total_budget) {
      formik.setFieldValue("funding_goal", total_budget);
    }
  }, []);

  return (
    <div
      className={`w-full flex flex-col ${containerClass} transition-all duration-300`}
    >
      <h3 className="text-xl font-medium abel-font"> Fundraising Method</h3>
      <div className="w-full flex flex-col mlg:flex-row justify-between mt-5 mlg:mt-10 mlg:mb-12 gap-y-4 max-lg:gap-x-14">
        <div className="w-full mlg:w-1/2 flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <CrowdfundingIcon className="w-12 h-12 flex-shrink-0" />
          <div className="flex flex-col">
            <span className="font-semibold text-lg">Crowdfunding</span>
            <span className="text-sm text-gray-600">
              A collaborative approach where researchers create projects, and contributors fund them, receiving commemorative NFTs in return. Every contribution counts in Crowdfunding, creating an NFT for projects receive funding regardless according to their final goal (Keep-it-all funding model)
            </span>
          </div>
        </div>
      </div>

      <section className="w-full flex flex-col mlg:flex-row justify-between mt-10 max-lg:gap-x-14 ">
        <div className="w-full mlg:w-1/2 lg:w-[45%] flex flex-col">
          <div className="w-full flex flex-col">
            {formik?.values.category !== "Asian-Based Grant Initiative for DeSci" && (
              <TextField
                disabled={
                  formik?.values?.visible || formik?.values?.nft_details?.address
                }
                type="number"
                id="funding_goal"
                value={
                  formik?.values.category === "Asian-Based Grant Initiative for DeSci"
                    ? 0
                    : formik?.values?.funding_goal
                }
                title="Fundraising Target"
                placeholder="Amount"
                containerClass="mb-6 mlg:mb-12 w-full"
                inputClass="h-12"
                formik={formik}
                onBlur={formik.handleBlur}
                icons={currencyIcons}
                currentIcon={"USD"}
                required
              />
            )}
            <div className="mb-6 mlg:mb-12 w-full">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-darkblue">
                  Minimum Funding Amount
                </span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formik.values.no_minimum_offer}
                    onChange={(e) => {
                      formik.setFieldValue("no_minimum_offer", e.target.checked);
                      if (e.target.checked) {
                        formik.setFieldValue("minimum_offer", "");
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ms-2 text-sm text-gray-500">
                    No Minimum
                  </span>
                </label>
              </div>
              {formik.values.no_minimum_offer ? (
                <div className="flex items-center justify-center h-12 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium">
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  No minimum funding required — anyone can contribute any amount
                </div>
              ) : (
                <TextField
                  type="number"
                  id={"minimum_offer"}
                  disabled={false}
                  value={formik.values.minimum_offer}
                  title={""}
                  placeholder={"Minimum Amount per Contribution"}
                  containerClass="w-full"
                  inputClass="h-12"
                  formik={formik}
                  onBlur={formik.handleBlur}
                  icons={currencyIcons}
                  currentIcon={"USD"}
                  required
                  tipsId="minimum_offer"
                />
              )}
            </div>
            <SelectBox
              id={"project_duration"}
              tipsId="crowdfunding_expiration"
              value={formik.values.project_duration}
              disabled={
                formik?.values?.visible ||
                formik?.values?.nft_details?.address
              }
              title={"Expiration Date"}
              options={CrowdfundingDurationOptions.map(({ unit, value }) => ({
                value: convertToMilliseconds({ unit, value }),
                title: value > 1 ? `${value} ${unit}s` : `value ${unit}`,
              }))}
              placeholder={"Select Date"}
              containerClass="w-full mb-6 mlg:mb-12"
              selectContainerClass="w-full h-12"
              formik={formik}
              onBlur={formik.handleBlur}
              required
            />
            <div className="w-full flex justify-between items-center bg-custom-gray-200 rounded-[10px] py-2 px-3 mb-6 mlg:mb-12">
              <div className="flex flex-col">
                <span className="text-[#636363] font-medium ">
                  Service fee
                </span>
                <span className="text-[#636363] text-xs">
                  The platform will charge a 4% service for each
                  NFT-Fundraising event.
                </span>
              </div>
              <span className="text-[#636363] font-medium min-w-fit pl-1.5">
                4 %
              </span>
            </div>
            <AttachFileField
              id={"attachment"}
              onChange={handleAttachFileChange}
              value={formik.values.attachment}
              title={"Supplementary Document"}
              placeholder={
                "Attach supplementary document (PDF file up to 2 MB)"
              }
              containerClass="mb-6 mlg:mb-12 w-full"
              inputClass="h-12"
              formik={formik}
              onBlur={formik.handleBlur}
              description="Attach supplementary document (PDF file up to 2 MB)"
              isLoading={uploadFileLoading}
            />
          </div>

          {/* ///////// */}
        </div>
        {/* ///////////////// */}
        <div className="w-full mlg:w-1/2 lg:w-[45%] flex flex-col justify-between h-full">
          {/* ////////// */}
          <div className="flex flex-col w-full">
            <span className="pl-2 mb-1 font-bold ">
              Add members to your team
            </span>
            <div className="w-full flex justify-between items-center my-2">
              <div className="w-1/2 flex items-center">
                <span
                  className={
                    " bg-[#e7d12e] rounded-full h-5 w-5 mr-1 flex justify-center items-center"
                  }
                >
                  <FaCheck color="white" className="text-[10px]" />
                </span>{" "}
                Invited{" "}
              </div>
              <div className="w-1/2 flex items-center">
                <span
                  className={
                    " bg-[#0fb263] rounded-full h-5 w-5 mr-1 flex justify-center items-center"
                  }
                >
                  <FaCheck color="white" className="text-[10px]" />
                </span>{" "}
                Approved
              </div>
            </div>
            <div className="w-full flex flex-col mb-4 border border-custom-gray-600 rounded-[10px] relative">
              <div className="relative w-full ">
                <div className="absolute right-2 top-0 h-full flex justify-center items-center">
                  <button
                    type="button"
                    onClick={handleInviteClick}
                    className="w-16 h-8 rounded-lg bg-primary text-sm text-white "
                  >
                    invite
                  </button>
                </div>

                <input
                  type="text"
                  id="searchTeamMember"
                  placeholder="Invite Team Member"
                  className="w-full non-appearance py-3 pl-4 pr-20 rounded-lg search-member-input"
                  value={searchValue}
                  onChange={filterBySearch}
                  onKeyDown={(e) => e?.key === "Enter" && e.preventDefault()}
                  onKeyUp={handleEnterSearch}
                  onFocus={() =>
                    !showSearchSuggestion &&
                    searchValue?.length &&
                    setShowSearchSuggestion(true)
                  }
                  onClick={() =>
                    !showSearchSuggestion &&
                    searchValue?.length &&
                    setShowSearchSuggestion(true)
                  }
                />
              </div>
              {showSearchSuggestion && (
                <div
                  ref={suggestionRef}
                  className="absolute top-[50px] w-full rounded-b-2xl shadow-lg max-h-60 overflow-y-auto custom-primary-tiny-scroll z-10 bg-white"
                >
                  {filteredUsers?.length ? (
                    filteredUsers.map((item, index, arr) => (
                      <div
                        onClick={() => {
                          setSearchValue(item?.email || item);
                          setShowSearchSuggestion(false);
                        }}
                        className={`w-full py-3 px-3 flex items-center hover:bg-slate-200  border-b border-custom-gray-300`}
                      >
                        <img
                          src={item?.images?.profile || UserPlaceHolderImg}
                          alt="user-img"
                          className="w-9 h-9 rounded-full"
                        />
                        <div className="w-full flex flex-col max-w-[90%] ml-3 ">
                          <span className="font-bold text-sm text-secondary">
                            {typeof item === "object"
                              ? `${
                                  item?.fundername ??
                                  `${item?.firstname} ${item?.lastname}`
                                }`
                              : item}
                          </span>
                          {item?.email ? (
                            <span className="text-xs">{item?.email}</span>
                          ) : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      onClick={() => {
                        setShowSearchSuggestion(false);
                      }}
                      className="w-full py-3 px-3 flex items-center hover:bg-slate-200  border-b border-custom-gray-300"
                    >
                      <img
                        src={UserPlaceHolderImg}
                        alt="user-img"
                        className="w-9 h-9 rounded-full"
                      />
                      <div className="w-full flex flex-col max-w-[90%] ml-3 ">
                        <span className="text-sm text-secondary font-bold">
                          {searchValue}
                        </span>

                        <span className="text-xs text-green-500">new</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="w-full max-h-[255px] overflow-y-scroll overflow-auto custom-primary-tiny-scroll ">
                {isLoading || getMeLoading ? (
                  <div className="w-full min-h-52 flex justify-center items-center">
                    <Bars
                      height="25"
                      width="25"
                      color="#8538FF"
                      ariaLabel="bars-loading"
                      wrapperStyle={{}}
                      wrapperClass=""
                      visible={true}
                    />
                  </div>
                ) : (
                  <div className="w-full flex flex-col ">
                    <MemberItem
                      user={formik?.values?.creator || userProfileData}
                      isCreator={true}
                      formik={formik}
                    />
                    {formik?.values?.all_collaborators?.map((user, index) => (
                      <MemberItem
                        user={user}
                        key={index}
                        onChange={handleSelectCollaborator}
                        formik={formik}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
            <p className="text-sm">
              To add members to your team, they must have an account on FunDeSci
              and confirm their participation by agreeing to the project
              invitation via email.{" "}
              <b>
                You can invite collaborators by entering their email addresses
                and sending them an invitation.
              </b>{" "}
              Once they accept the invitation and confirm their participation,
              they will be added to your team.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Step3;
